import type { BoardNode, ChipType, CircuitPuzzlePayload, Hint } from "@monekineko/game-engine";
import { SOCKET_OF, evaluateBoard } from "@monekineko/game-engine";
import {
  $,
  $$,
  Cutscene,
  actorMarkup,
  rand,
  sfx,
  shuffle,
  sleep,
  spawnSpark,
  vibrate,
  virusHide,
  virusParts,
  virusReset,
  virusRun,
  virusScare,
  virusTinker,
} from "./core.js";
import type { EngineContext, GameEngine } from "./types.js";

// Уровень «Пазл микросхем». Перенос prototypes/1/js/level3.js. Плата приходит
// с сервера готовой, включая набор микросхем; решение проверяет сервер.

const CHIP_VIEW: Record<ChipType, { sym: string; cap: string }> = {
  rep: { sym: "▷", cap: "ПОВТ" },
  inv: { sym: "▷∘", cap: "НЕ" },
  and: { sym: "И", cap: "" },
  or: { sym: "ИЛИ", cap: "" },
};

const ICONS = {
  source:
    '<path d="M11 21h-1l1-7H7.5c-.6 0-.6-.3-.4-.7.2-.4.1-.2.1-.3C8.5 10.3 10.3 7 13 2h1l-1 7h3.5c.5 0 .6.3.4.8l-.1.2C12.8 18.1 11 21 11 21z"/>',
  lamp: '<path d="M9 21c0 .6.4 1 1 1h4c.6 0 1-.4 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .6.4 1 1 1h6c.6 0 1-.4 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z"/>',
};

const svgIcon = (path: string): string => `<svg class="ico" viewBox="0 0 24 24" aria-hidden="true">${path}</svg>`;

const markup = (): string => `
  <div class="screen active" data-level="circuit_puzzle">
    <div class="l3">
      <div class="l3-back"></div>
      <div class="l3-layout">
        <div class="board-area">
          <div class="board"><svg class="traces" viewBox="0 0 5 10" preserveAspectRatio="none"></svg></div>
          <div class="lid"></div>
        </div>
        <div class="tray-wrap">
          <div class="tray"></div>
          <div class="l3-hint"></div>
        </div>
      </div>
      ${actorMarkup("backHidden")}
      <div class="fx"></div>
    </div>
    <div class="l3-city" hidden>
      <div class="l3-city-stage"></div>
      <div class="rain"></div>
    </div>
    <div class="whiteout"></div>
  </div>`;

export function createCircuitPuzzle(root: HTMLElement, ctx: EngineContext): GameEngine {
  const payload = ctx.payload as CircuitPuzzlePayload;
  root.innerHTML = markup();

  const l3 = $(".l3", root)!;
  const boardArea = $(".board-area", root)!;
  const boardEl = $(".board", root)!;
  const traces = $<SVGSVGElement>(".traces", root)! as unknown as SVGSVGElement;
  const tray = $(".tray", root)!;
  const hintEl = $(".l3-hint", root)!;
  const lid = $(".lid", root)!;
  const city = $(".l3-city", root)!;
  const cityStage = $(".l3-city-stage", root)!;
  const whiteout = $(".whiteout", root)!;
  const fx = $(".l3 > .fx", root)!;
  const virus = virusParts($(".l3 > .actor", root)!);

  const state = {
    chips: [] as { id: string; type: ChipType }[],
    placed: {} as Record<string, string>,
    phase: "idle" as "idle" | "intro" | "play" | "done",
    selected: null as string | null,
    drag: null as null | {
      el: HTMLElement;
      id: string;
      pointerId: number;
      dx: number;
      dy: number;
      x0: number;
      y0: number;
      size: number;
      moved: boolean;
    },
  };

  let cut: Cutscene | null = null;
  let alive = true;
  const nodeEls: Record<string, HTMLElement> = {};
  const chipEls: Record<string, HTMLElement> = {};
  const linkEls: { link: { from: string; to: string }; glow: SVGPathElement; line: SVGPathElement }[] = [];

  function setPhase(phase: typeof state.phase): void {
    state.phase = phase;
    l3.dataset.phase = phase;
  }

  // ---------- Построение платы ----------

  function corridorPaths(): Map<{ from: string; to: string }, { index: number; total: number }> {
    // Горизонтальные участки дорожек разводим по «каналам», чтобы они не сливались
    const byParentRow: Record<number, { from: string; to: string }[]> = {};
    payload.links.forEach((link) => {
      const from = payload.nodes.find((node) => node.id === link.from)!;
      (byParentRow[from.row] ||= []).push(link);
    });
    const channel = new Map<{ from: string; to: string }, { index: number; total: number }>();
    Object.values(byParentRow).forEach((group) => {
      group.forEach((link, index) => channel.set(link, { index, total: group.length }));
    });
    return channel;
  }

  function buildBoard(): void {
    l3.style.setProperty("--cols", String(payload.cols));
    l3.style.setProperty("--rows", String(payload.rows));
    traces.setAttribute("viewBox", `0 0 ${payload.cols} ${payload.rows}`);
    traces.setAttribute("preserveAspectRatio", "none");
    traces.replaceChildren();

    const at: Record<string, BoardNode> = {};
    payload.nodes.forEach((node) => {
      at[node.id] = node;
    });
    const channel = corridorPaths();

    payload.links.forEach((link) => {
      const from = at[link.from]!;
      const to = at[link.to]!;
      const x1 = from.col + 0.5;
      const y1 = from.row + 0.5;
      const x2 = to.col + 0.5;
      const y2 = to.row + 0.5;
      let d: string;
      if (from.col === to.col) {
        d = `M${x1} ${y1} L${x2} ${y2}`;
      } else {
        const { index, total } = channel.get(link)!;
        const top = from.row + 1;
        const height = to.row - from.row - 1;
        const y = top + ((index + 0.5) / total) * Math.max(height, 0.9);
        d = `M${x1} ${y1} V${y} H${x2} V${y2}`;
      }
      const glow = document.createElementNS("http://www.w3.org/2000/svg", "path");
      glow.setAttribute("d", d);
      glow.setAttribute("class", "trace glow");
      const line = document.createElementNS("http://www.w3.org/2000/svg", "path");
      line.setAttribute("d", d);
      line.setAttribute("class", "trace");
      traces.append(glow, line);
      linkEls.push({ link, glow, line });
    });

    payload.nodes.forEach((node) => {
      const el = document.createElement("div");
      el.className = `node ${node.kind}`;
      el.dataset.id = node.id;
      el.style.setProperty("--c", String(node.col));
      el.style.setProperty("--r", String(node.row));
      if (node.kind === "source") el.innerHTML = svgIcon(ICONS.source);
      else if (node.kind === "lamp") el.innerHTML = svgIcon(ICONS.lamp);
      else {
        el.classList.add("socket", node.kind === "gate" ? "gate" : "linear");
        el.innerHTML = '<div class="frame"></div>';
      }
      boardEl.appendChild(el);
      nodeEls[node.id] = el;
    });
  }

  function buildChips(): void {
    tray.replaceChildren();
    state.chips = [];
    state.placed = {};
    state.selected = null;

    let index = 0;
    const list: { id: string; type: ChipType }[] = [];
    (Object.entries(payload.chips) as [ChipType, number][]).forEach(([type, count]) => {
      for (let i = 0; i < (count ?? 0); i++) list.push({ id: `c${index++}`, type });
    });
    shuffle(list);
    state.chips = list;

    list.forEach((chip) => {
      const el = document.createElement("div");
      el.className = "chip";
      el.dataset.id = chip.id;
      el.dataset.type = chip.type;
      const view = CHIP_VIEW[chip.type];
      el.innerHTML = `<span class="sym">${view.sym}</span>${view.cap ? `<span class="cap">${view.cap}</span>` : ""}`;
      chipEls[chip.id] = el;
      tray.appendChild(el);
    });
  }

  // Клетка подгоняется под свободное место над полкой
  function fitBoard(): void {
    const rect = boardArea.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const cell = Math.floor(Math.min((rect.width - 8) / payload.cols, (rect.height - 8) / payload.rows));
    l3.style.setProperty("--cell", `${Math.max(22, cell)}px`);
  }
  const observer = new ResizeObserver(fitBoard);
  observer.observe(boardArea);

  // ---------- Сигнал ----------

  function placement(): Record<string, ChipType> {
    const result: Record<string, ChipType> = {};
    for (const [socketId, chipId] of Object.entries(state.placed)) {
      const chip = state.chips.find((item) => item.id === chipId);
      if (chip) result[socketId] = chip.type;
    }
    return result;
  }

  function render(): Record<string, boolean> {
    const values = evaluateBoard(payload.nodes, placement());
    linkEls.forEach(({ link, glow, line }) => {
      const live = Boolean(values[link.from]);
      line.classList.toggle("live", live);
      glow.classList.toggle("live", live);
    });
    payload.nodes.forEach((node) => {
      const el = nodeEls[node.id]!;
      if (node.kind === "lamp") el.classList.toggle("lit", Boolean(values[node.id]));
      if (node.kind === "linear" || node.kind === "gate") {
        el.classList.toggle("filled", Boolean(state.placed[node.id]));
        const chipId = state.placed[node.id];
        if (chipId) chipEls[chipId]?.classList.toggle("hot", Boolean(values[node.id]));
      }
    });
    return values;
  }

  function updateHint(values: Record<string, boolean>): void {
    const lamps = payload.nodes.filter((node) => node.kind === "lamp");
    const lit = lamps.filter((node) => values[node.id]).length;
    const left = state.chips.length - Object.keys(state.placed).length;
    hintEl.classList.remove("ok");
    if (state.phase !== "play") return;
    hintEl.textContent =
      left > 0
        ? `Осталось разложить микросхем: ${left} · лампочек горит: ${lit} из ${lamps.length}`
        : `Все микросхемы на плате · лампочек горит: ${lit} из ${lamps.length}`;
  }

  function refresh(): void {
    const values = render();
    updateHint(values);
    if (state.phase !== "play") return;
    const allPlaced = Object.keys(state.placed).length === state.chips.length;
    const allLit = payload.nodes.filter((node) => node.kind === "lamp").every((node) => values[node.id]);
    if (allPlaced && allLit) void solved();
  }

  // ---------- Раскладка ----------

  const socketKind = (socketId: string): string =>
    payload.nodes.find((node) => node.id === socketId)!.kind;

  function fits(chipId: string, socketId: string): boolean {
    const chip = state.chips.find((item) => item.id === chipId)!;
    return SOCKET_OF[chip.type] === socketKind(socketId);
  }

  function toTray(chipId: string): void {
    const socketId = Object.keys(state.placed).find((id) => state.placed[id] === chipId);
    if (socketId) delete state.placed[socketId];
    tray.appendChild(chipEls[chipId]!);
  }

  function place(chipId: string, socketId: string): boolean {
    if (!fits(chipId, socketId)) {
      sfx.deny();
      return false;
    }
    const busy = state.placed[socketId];
    if (busy && busy !== chipId) toTray(busy);
    const was = Object.keys(state.placed).find((id) => state.placed[id] === chipId);
    if (was) delete state.placed[was];
    state.placed[socketId] = chipId;
    nodeEls[socketId]!.appendChild(chipEls[chipId]!);
    sfx.drop();
    vibrate(10);
    ctx.event("chip_placed", { socket: socketId });
    return true;
  }

  function select(chipId: string | null): void {
    if (state.selected) chipEls[state.selected]?.classList.remove("selected");
    state.selected = chipId;
    if (chipId) chipEls[chipId]?.classList.add("selected");
  }

  // ---------- Перетаскивание пальцем ----------

  function onPointerDown(event: PointerEvent): void {
    if (state.phase !== "play") return;
    const target = event.target as HTMLElement;
    const chipEl = target.closest<HTMLElement>(".chip");
    if (!chipEl) {
      const socket = target.closest<HTMLElement>(".socket");
      if (socket && state.selected) {
        const chipId = state.selected;
        select(null);
        if (place(chipId, socket.dataset.id!)) refresh();
      }
      return;
    }
    event.preventDefault();
    const rect = chipEl.getBoundingClientRect();
    state.drag = {
      el: chipEl,
      id: chipEl.dataset.id!,
      pointerId: event.pointerId,
      dx: event.clientX - rect.left,
      dy: event.clientY - rect.top,
      x0: event.clientX,
      y0: event.clientY,
      size: rect.width,
      moved: false,
    };
    chipEl.setPointerCapture(event.pointerId);
    sfx.pick();
  }

  const dropTarget = (event: PointerEvent): HTMLElement | null => {
    const el = document.elementFromPoint(event.clientX, event.clientY);
    return el ? el.closest<HTMLElement>(".socket") : null;
  };

  function onPointerMove(event: PointerEvent): void {
    const drag = state.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!drag.moved && Math.hypot(event.clientX - drag.x0, event.clientY - drag.y0) < 6) return;
    if (!drag.moved) {
      drag.moved = true;
      drag.el.style.width = `${drag.size}px`;
      drag.el.style.height = `${drag.size}px`;
      drag.el.style.setProperty("--size", `${drag.size}px`);
      drag.el.classList.add("dragging");
    }
    drag.el.style.left = `${event.clientX - drag.dx}px`;
    drag.el.style.top = `${event.clientY - drag.dy}px`;

    const socket = dropTarget(event);
    $$<HTMLElement>(".socket", boardEl).forEach((element) => element.classList.remove("drop", "deny"));
    if (socket) socket.classList.add(fits(drag.id, socket.dataset.id!) ? "drop" : "deny");
  }

  function onPointerUp(event: PointerEvent): void {
    const drag = state.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    state.drag = null;
    $$<HTMLElement>(".socket", boardEl).forEach((element) => element.classList.remove("drop", "deny"));

    if (!drag.moved) {
      // короткое касание: выбрать в полке или снять с платы
      if (drag.el.parentElement === tray) select(state.selected === drag.id ? null : drag.id);
      else {
        select(null);
        toTray(drag.id);
        sfx.pick();
        refresh();
      }
      return;
    }

    const socket = dropTarget(event);
    drag.el.classList.remove("dragging");
    drag.el.style.left = "";
    drag.el.style.top = "";
    drag.el.style.width = "";
    drag.el.style.height = "";
    drag.el.style.removeProperty("--size");
    select(null);
    if (socket) place(drag.id, socket.dataset.id!);
    else toTray(drag.id);
    refresh();
  }

  l3.addEventListener("pointerdown", onPointerDown);
  l3.addEventListener("pointermove", onPointerMove);
  l3.addEventListener("pointerup", onPointerUp);
  l3.addEventListener("pointercancel", onPointerUp);

  // ---------- Финал ----------

  async function solved(): Promise<void> {
    setPhase("done");
    select(null);

    const result = await ctx.submit({ placement: placement() });
    if (!alive) return;

    if (!result.solved) {
      sfx.error();
      hintEl.textContent = "Схема не принята. Начни уровень заново";
      await sleep(900);
      if (!alive) return;
      ctx.finished(result);
      return;
    }

    boardEl.classList.add("solved");
    hintEl.classList.add("ok");
    hintEl.textContent = "Схема собрана! Питание дошло до всех лампочек";
    sfx.success();
    vibrate([30, 60, 30]);
    await sleep(1800);
    if (!alive) return;

    lid.classList.add("closed");
    sfx.cabinetClose(1.45);
    await sleep(1900);
    if (!alive) return;

    sfx.whoosh();
    whiteout.classList.add("show");
    await sleep(1000);
    if (!alive) return;
    ctx.finished(result);
  }

  // ---------- Заставка ----------

  function setLid(closed: boolean, animated: boolean): void {
    if (!animated) l3.classList.add("no-anim");
    lid.classList.toggle("closed", closed);
    if (!animated) {
      void lid.offsetWidth;
      l3.classList.remove("no-anim");
    }
  }

  function setWhite(on: boolean, animated: boolean): void {
    if (!animated) whiteout.style.transition = "none";
    whiteout.classList.toggle("show", on);
    if (!animated) {
      void whiteout.offsetWidth;
      whiteout.style.transition = "";
    }
  }

  async function playIntro(): Promise<void> {
    cut = new Cutscene();
    const c = cut;
    setPhase("intro");

    tray.replaceChildren(); // микросхемы вирус ещё держит при себе
    hintEl.textContent = "";
    virusReset(virus, "backHidden");
    city.hidden = false;
    setWhite(true, false);

    try {
      // Тот же город, теперь посреди улицы сидит робот
      await c.later(50);
      setWhite(false, true);
      sfx.city();
      c.track(
        cityStage.animate([{ transform: "scale(1)" }, { transform: "scale(1.14)" }], {
          duration: 3000,
          easing: "ease-out",
          fill: "forwards",
        }),
      );
      await c.later(2600);

      // Подходим к роботу со спины
      sfx.zoom();
      c.track(
        cityStage.animate([{ transform: "scale(1.14)" }, { transform: "scale(3.6)" }], {
          duration: 1300,
          easing: "cubic-bezier(.6,0,.9,.5)",
          fill: "forwards",
        }),
      );
      c.track(
        city.animate([{ opacity: 1 }, { opacity: 1, offset: 0.5 }, { opacity: 0 }], {
          duration: 1300,
          fill: "forwards",
        }),
      );
      c.track(
        l3.animate([{ transform: "scale(1.25)" }, { transform: "scale(1)" }], {
          duration: 1800,
          easing: "cubic-bezier(.4,0,.2,1)",
          fill: "forwards",
        }),
      );

      // Вирус копается в спине
      const tinker = virusTinker(c, virus);
      const sparks = c.every(110, () => {
        spawnSpark(fx, rand(38, 62), rand(36, 56));
        if (Math.random() < 0.5) sfx.tinker();
      });
      await c.later(1300);
      city.hidden = true;
      await c.later(1700);
      clearInterval(sparks);

      await virusScare(c, virus, tinker);
      virusRun(c, virus);
      await c.later(700);

      // Убегая, вирус бросает микросхемы на полку
      for (const chip of state.chips) {
        const el = chipEls[chip.id]!;
        tray.appendChild(el);
        el.classList.add("landing");
        sfx.drop();
        await c.later(90);
      }
      await c.later(400);
    } catch {
      return;
    }
    finishIntro();
  }

  function stopIntro(): void {
    if (!cut) return;
    cut.stop();
    cut = null;
    fx.replaceChildren();
    virusHide(virus);
  }

  function showLevel(): void {
    city.hidden = true;
    setWhite(false, false);
    state.chips.forEach((chip) => {
      const el = chipEls[chip.id]!;
      el.classList.remove("landing");
      if (!Object.values(state.placed).includes(chip.id)) tray.appendChild(el);
    });
    setPhase("play");
    fitBoard();
    refresh();
  }

  function finishIntro(): void {
    stopIntro();
    showLevel();
    ctx.introDone();
  }

  buildBoard();
  buildChips();
  setLid(false, false);
  render();

  if (ctx.playIntro) void playIntro();
  else finishIntro();

  return {
    destroy() {
      alive = false;
      stopIntro();
      observer.disconnect();
      root.innerHTML = "";
    },
    skipIntro: finishIntro,
    canHint: () => state.phase === "play",
    applyHint(hint: Hint) {
      const data = hint.data as { socket: string; chip: ChipType };
      const socket = nodeEls[data.socket];
      hintEl.textContent = hint.text;
      hintEl.classList.add("ok");
      if (!socket) return;
      socket.animate(
        [
          { boxShadow: "0 0 0 rgba(62,240,255,0)" },
          { boxShadow: "0 0 24px 6px rgba(62,240,255,0.8)" },
          { boxShadow: "0 0 0 rgba(62,240,255,0)" },
        ],
        { duration: 900, iterations: 3 },
      );
    },
  };
}
